import type { components } from '@bookingdei/api-client/types';
import type { Lang } from '@bookingdei/i18n/translate';
import { formatDate } from '@bookingdei/i18n/format-date';
import { stepperHref } from './stepper-href';
import type { OfferFormat } from './use-offer-format';

type OfferCard = components['schemas']['OfferCard'];
type DirectoryOffer = components['schemas']['DirectoryOfferCard'];
type ImageVariants = components['schemas']['ImageVariants'];

// What the poster card and the offer popup draw. A platform offer (OfferCard, API-52)
// and a deal a business announced itself (DirectoryOfferCard, API-236) carry different
// shapes and different actions; both are read once into this one view so a card and
// a popup never branch on the source. Every figure here is a server-computed string
// already run through the i18n formatters — nothing below derives a price (DB-48).
export interface OfferView {
  key: string;
  kind: 'offer' | 'deal';
  /** The headline on the card and in the popup ("Eat 5 Pay 1 dinner buffet…"). */
  title: string;
  bizName: string;
  /** Business page to open from the popup; a deal with no listing has none. */
  bizHref?: string;
  /** The listing's slug and this offer's id — what the popup's reel rail asks for (FR-C29). */
  bizSlug?: string;
  offerId?: string;
  category: string;
  area: string;
  /** Poster (card) and stage (popup) creative; the full one feeds the lightbox. */
  image?: { poster: string; full: string };
  /** "40% OFF" / "৳500 OFF" / "Deal". */
  off: string;
  ends: { label: string; urgent: boolean };
  priceBefore?: string;
  priceAfter?: string;
  terms?: string;
  /** "Valid till 2 Nov 2026" / "15 Oct – 2 Nov 2026" — the offer window (FR-C19). */
  validity: string;
  /** Deep link into the booking stepper for a bookable offer (API-62). */
  bookHref?: string;
  /** The offer covers every service at the listing, or every listing of the merchant (DR-78). */
  appliesTo?: 'business' | 'account';
  /** What a deal asks people to do instead of booking (FR-A26 v3). */
  cta?: DirectoryOffer['cta'];
  /** The number behind a `tel:` CTA, shown with a copy button. */
  phone?: string;
  sponsored: boolean;
  verified: boolean;
  /** Short line under the price: "5h ago · 1.2 km". */
  meta: string;
}

const pick = (img: ImageVariants | undefined) => {
  const poster = img?.['800'] ?? img?.['400'] ?? img?.['1200'];
  if (!poster) return undefined;
  return { poster, full: img?.['1200'] ?? poster };
};

// Window formatted at day granularity: a running offer shows only its end, one that has
// not started yet shows both ends so the reader knows when it opens.
export function offerValidity(lang: Lang, startsAt: string, endsAt: string, t: (k: 'dc52.popup.valid_till' | 'dc52.popup.valid_range', v: Record<string, string>) => string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return '';
  const till = formatDate(lang, end);
  if (Number.isNaN(start.getTime()) || start.getTime() <= Date.now()) return t('dc52.popup.valid_till', { date: till });
  return t('dc52.popup.valid_range', { from: formatDate(lang, start, { year: start.getFullYear() !== end.getFullYear() }), to: till });
}

export interface ViewContext {
  lang: Lang;
  fmt: OfferFormat;
  t: (k: 'dc52.popup.valid_till' | 'dc52.popup.valid_range' | 'dc52.deal' | 'dc52.off' | 'dc52.off_amount', v?: Record<string, string>) => string;
  /** Category label by registry id (the registry is the only source of category names). */
  catLabel: (id: string | undefined) => string;
}

export function offerView(o: OfferCard, ctx: ViewContext): OfferView {
  const { fmt, t, lang } = ctx;
  const hasPrice = fmt.hasPrice(o);
  return {
    key: `o-${o.id}`,
    kind: 'offer',
    title: fmt.deal(o),
    bizName: fmt.bizName(o),
    bizHref: `/b/${o.business.slug}`,
    bizSlug: o.business.slug,
    offerId: o.id,
    category: ctx.catLabel(o.category_id ?? o.business.category_id),
    area: o.business.area_name ?? '',
    image: pick(o.image) ?? pick(o.business.cover_image),
    off: fmt.off(o),
    ends: fmt.ends(o),
    ...(hasPrice ? { priceBefore: fmt.money(o.price_before_minor), priceAfter: fmt.money(o.price_after_minor) } : {}),
    ...(o.terms ? { terms: o.terms } : {}),
    validity: offerValidity(lang, o.starts_at ?? '', o.ends_at, t),
    ...(o.bookable ? { bookHref: stepperHref(o) } : {}),
    ...(o.applies_to === 'business' || o.applies_to === 'account' ? { appliesTo: o.applies_to } : {}),
    sponsored: Boolean(o.sponsored),
    verified: Boolean(o.business.verified),
    meta: [fmt.posted(o), o.distance_label ?? ''].filter(Boolean).join(' · '),
  };
}

export function dealView(d: DirectoryOffer, ctx: ViewContext): OfferView {
  const { fmt, t, lang } = ctx;
  const off =
    d.kind === 'percent' && d.discount_value
      ? t('dc52.off', { n: fmt.num(d.discount_value) })
      : d.kind === 'fixed' && d.discount_value
        ? t('dc52.off_amount', { amount: fmt.money(d.discount_value) })
        : t('dc52.deal');
  const listing = d.cta.kind === 'listing' ? d.cta.href : undefined;
  const phone = d.cta.kind === 'call' && d.cta.href.startsWith('tel:') ? d.cta.href.slice(4) : undefined;
  return {
    key: `d-${d.id}`,
    kind: 'deal',
    title: d.title,
    bizName: d.business?.name ?? d.advertiser_name,
    ...(listing ? { bizHref: listing } : {}),
    category: ctx.catLabel(d.category_id),
    area: d.area_name ?? '',
    image: pick(d.image),
    off,
    ends: fmt.ends(d),
    ...(d.terms ? { terms: d.terms } : {}),
    validity: offerValidity(lang, d.starts_at, d.ends_at, t),
    cta: d.cta,
    ...(phone ? { phone } : {}),
    sponsored: false,
    verified: false,
    meta: '',
  };
}
